import { createSelector } from "reselect"

import { createReducer } from "redux/utility"
import { outerJoin } from "lib/utils/helpers/data-sets"

import { selectSelectedHash as selectSelectedJourneyHash } from "redux/reducers/entities/journeys"
import { selectIsRepublishing } from "redux/reducers/ui/journeyEdit"

import { itemBaseType } from "constants/conditionRules"

import { createEntitiesFeatureSelector } from "../helpers"

import * as selectors from "./selectors"
import * as clonedJourneySelectors from "./prerequisitesClone/selectors"
import { initialState, reducers } from "./reducers"
import { reducerName } from "./actionNames"

export * from "./reducers"
export * from "./schema"

// selectors
export const selectFeature = createEntitiesFeatureSelector(reducerName)

export const selectNormalData = createSelector(
    selectFeature,
    selectors.selectData,
)
export const selectClonedData = createSelector(
    selectFeature,
    clonedJourneySelectors.selectData,
)

const selectNormalIdentifiers = createSelector(
    selectFeature,
    selectors.selectIdentifiers,
)
const selectClonedIdentifiers = createSelector(
    selectFeature,
    clonedJourneySelectors.selectIdentifiers,
)

export const selectData = createSelector(
    selectIsRepublishing,
    selectNormalData,
    selectClonedData,
    (isRepublishing, normal, cloned) => (isRepublishing ? cloned : normal),
)

export const selectIdentifiers = createSelector(
    selectIsRepublishing,
    selectNormalIdentifiers,
    selectClonedIdentifiers,
    (isRepublishing, normal, cloned) => (isRepublishing ? cloned : normal),
)
export const selectTalentIdentifiers = createSelector(
    selectFeature,
    selectors.selectTalentIdentifiers,
)
export const selectHasFetched = createSelector(
    selectFeature,
    selectors.selectHasFetched,
)
export const selectIsLoading = createSelector(
    selectFeature,
    selectors.selectIsLoading,
)
export const selectLoadingError = createSelector(
    selectFeature,
    selectors.selectLoadingError,
)
export const selectSelectedHash = createSelector(
    selectFeature,
    selectors.selectSelectedHash,
)

export const selectPrerequisites = createSelector(
    selectIdentifiers,
    selectData,
    (identifiers, data) => outerJoin(identifiers, data).reverse(),
)

export const selectHasJourneyPrerequisites = createSelector(
    selectPrerequisites,
    selectSelectedJourneyHash,
    (prerequisites, journeyHash) =>
        prerequisites?.some(
            prerequisite => prerequisite?.journeyHash === journeyHash,
        ),
)

export const makeSelectPrerequisiteByHash = () =>
    createSelector(
        selectData,
        (state, hash) => hash,
        (prerequisites, hash) => prerequisites?.[hash],
    )

export const makeSelectorPrerequisiteByTargetHash = () =>
    createSelector(
        selectPrerequisites,
        (state, targetHash) => targetHash,
        (prerequisites, targetHash) =>
            prerequisites.find(
                prerequisite => prerequisite?.targetHash === targetHash,
            ),
    )

export const selectJourneyEventsPrerequisites = createSelector(
    selectPrerequisites,
    selectSelectedJourneyHash,
    (prerequisites, journeyHash) =>
        prerequisites.reduce(
            (result, prerequisite) =>
                prerequisite.sourceType === itemBaseType.journey &&
                prerequisite.targetType === itemBaseType.stepMessageItem &&
                prerequisite.journeyHash === journeyHash
                    ? {
                          ...result,
                          [prerequisite.userProgressState]: prerequisite,
                      }
                    : result,
            {},
        ),
)

export const reducer = createReducer(reducers, initialState)
